import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Empty,
  Grid,
  Radio,
  Row,
  Space,
  Spin,
  Switch,
  TimePicker,
  Typography,
  message,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { trpc } from '../../lib/api';

const { useBreakpoint } = Grid;
const { Text, Title } = Typography;

const CATEGORY_LABELS: Record<string, string> = {
  maintenance: 'メンテナンス',
  alert: 'アラート',
  account: 'アカウント',
  system: 'システム',
};

const DIGEST_OPTIONS = [
  { label: '毎日', value: 'daily' },
  { label: '週1回', value: 'weekly' },
];

const timeFormat = 'HH:mm';

const formatTimeForApi = (value: Dayjs | null): string | null =>
  value ? `${value.format('HH:mm')}:00` : null;

dayjs.extend(customParseFormat);

type ChannelField = 'channelInApp' | 'channelEmail';

export function NotificationSettings() {
  const screens = useBreakpoint();
  const [messageApi, contextHolder] = message.useMessage();
  const utils = trpc.useContext();

  const {
    data,
    error,
    isLoading,
    isFetching,
  } = trpc.notificationPreferences.list.useQuery(undefined, {
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const updateCategoryMutation = trpc.notificationPreferences.updateCategory.useMutation({
    onSuccess: async () => {
      await utils.notificationPreferences.list.invalidate();
    },
    onError: (err) => {
      messageApi.error(err.message ?? '通知設定の更新に失敗しました');
    },
  });

  const updatePreferenceMutation = trpc.notificationPreferences.update.useMutation({
    onSuccess: async () => {
      await utils.notificationPreferences.list.invalidate();
    },
    onError: (err) => {
      messageApi.error(err.message ?? '通知設定の更新に失敗しました');
    },
  });

  const preferences = useMemo(() => data ?? [], [data]);
  const firstPreference = preferences[0];

  const [digestEnabled, setDigestEnabled] = useState(false);
  const [digestFrequency, setDigestFrequency] = useState<string>('daily');
  const [quietHoursEnabled, setQuietHoursEnabled] = useState(false);
  const [quietHoursStart, setQuietHoursStart] = useState<Dayjs | null>(dayjs('22:00', timeFormat));
  const [quietHoursEnd, setQuietHoursEnd] = useState<Dayjs | null>(dayjs('07:00', timeFormat));
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!firstPreference) {
      return;
    }

    setDigestEnabled(firstPreference.frequency !== 'realtime');
    setDigestFrequency(
      firstPreference.frequency && firstPreference.frequency !== 'realtime'
        ? firstPreference.frequency
        : 'daily'
    );

    setQuietHoursEnabled(firstPreference.quietHoursEnabled ?? false);
    setQuietHoursStart(
      firstPreference.quietHoursStart
        ? dayjs(firstPreference.quietHoursStart, 'HH:mm:ss')
        : dayjs('22:00', timeFormat)
    );
    setQuietHoursEnd(
      firstPreference.quietHoursEnd
        ? dayjs(firstPreference.quietHoursEnd, 'HH:mm:ss')
        : dayjs('07:00', timeFormat)
    );
  }, [firstPreference]);

  const allInAppEnabled = useMemo(
    () => preferences.every((pref) => pref.channelInApp),
    [preferences]
  );
  const allEmailEnabled = useMemo(
    () => preferences.every((pref) => pref.channelEmail),
    [preferences]
  );

  const handleGlobalChannelToggle = async (field: ChannelField, enabled: boolean) => {
    if (!preferences.length) {
      return;
    }
    setPending(true);
    try {
      await Promise.all(
        preferences.map((pref) =>
          updateCategoryMutation.mutateAsync({
            category: pref.category,
            [field]: enabled,
          } as Record<string, unknown>)
        )
      );
      messageApi.success(
        `通知方法（${field === 'channelInApp' ? 'サイト内' : 'メール'}）を${
          enabled ? '有効' : '無効'
        }にしました`
      );
    } finally {
      setPending(false);
    }
  };

  const handleCategoryToggle = async (
    category: string,
    field: ChannelField,
    enabled: boolean
  ) => {
    await updateCategoryMutation.mutateAsync({
      category,
      [field]: enabled,
    } as Record<string, unknown>);
    messageApi.success('カテゴリ別の通知設定を更新しました');
  };

  const updateAllPreferences = async (payload: Record<string, unknown>) => {
    if (!preferences.length) {
      return;
    }
    setPending(true);
    try {
      await Promise.all(
        preferences.map((pref) =>
          updatePreferenceMutation.mutateAsync({
            category: pref.category,
            ...payload,
          })
        )
      );
    } finally {
      setPending(false);
    }
  };

  const handleDigestToggle = async (enabled: boolean) => {
    setDigestEnabled(enabled);
    await updateAllPreferences({
      frequency: enabled ? digestFrequency : 'realtime',
    });
    messageApi.success('配信設定を更新しました');
  };

  const handleDigestFrequencyChange = async (value: string) => {
    setDigestFrequency(value);
    if (!digestEnabled) {
      return;
    }
    await updateAllPreferences({
      frequency: value,
    });
    messageApi.success('配信頻度を更新しました');
  };

  const handleQuietHoursToggle = async (enabled: boolean) => {
    setQuietHoursEnabled(enabled);
    await updateAllPreferences({
      quietHoursEnabled: enabled,
      quietHoursStart: enabled ? formatTimeForApi(quietHoursStart) : null,
      quietHoursEnd: enabled ? formatTimeForApi(quietHoursEnd) : null,
    });
    messageApi.success('休止時間設定を更新しました');
  };

  const handleQuietHoursChange = async (field: 'start' | 'end', value: Dayjs | null) => {
    const nextStart = field === 'start' ? value : quietHoursStart;
    const nextEnd = field === 'end' ? value : quietHoursEnd;
    if (field === 'start') {
      setQuietHoursStart(value);
    } else {
      setQuietHoursEnd(value);
    }

    if (!quietHoursEnabled) {
      return;
    }

    await updateAllPreferences({
      quietHoursEnabled: true,
      quietHoursStart: formatTimeForApi(nextStart),
      quietHoursEnd: formatTimeForApi(nextEnd),
    });
    messageApi.success('休止時間を更新しました');
  };

  const renderChannelSwitch = (
    label: string,
    field: ChannelField,
    enabled: boolean,
    ariaLabel: string
  ) => (
    <Space align="center">
      <Text>{label}</Text>
      <Switch
        checked={enabled}
        loading={pending || updateCategoryMutation.isPending}
        onChange={(checked) => void handleGlobalChannelToggle(field, checked)}
        aria-label={ariaLabel}
      />
    </Space>
  );

  const renderCategoryRow = (category: string, inApp: boolean, email: boolean) => {
    const label = CATEGORY_LABELS[category] ?? category;
    return (
      <Row
        key={category}
        align="middle"
        justify="space-between"
        style={{ padding: screens.md ? '12px 0' : '12px 8px' }}
      >
        <Col xs={24} md={8}>
          <Text strong>{label}</Text>
        </Col>
        <Col xs={12} md={8}>
          <Space>
            <Text>サイト内</Text>
            <Switch
              checked={inApp}
              onChange={(checked) => void handleCategoryToggle(category, 'channelInApp', checked)}
              loading={updateCategoryMutation.isPending}
              aria-label={`${label}-サイト内`}
            />
          </Space>
        </Col>
        <Col xs={12} md={8}>
          <Space>
            <Text>メール</Text>
            <Switch
              checked={email}
              onChange={(checked) => void handleCategoryToggle(category, 'channelEmail', checked)}
              loading={updateCategoryMutation.isPending}
              aria-label={`${label}-メール`}
            />
          </Space>
        </Col>
      </Row>
    );
  };

  return (
    <>
      {contextHolder}
      <Spin spinning={isLoading || isFetching}>
        {error ? (
          <Alert
            type="error"
            message="通知設定の取得に失敗しました"
            description={error.message}
            showIcon
          />
        ) : preferences.length === 0 ? (
          <Empty description="通知設定が見つかりません" />
        ) : (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card title="通知方法">
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  {renderChannelSwitch('サイト内通知', 'channelInApp', allInAppEnabled, 'サイト内通知全体')}
                </Col>
                <Col xs={24} md={12}>
                  {renderChannelSwitch('メール通知', 'channelEmail', allEmailEnabled, 'メール通知全体')}
                </Col>
              </Row>
              <Divider />
              <Space direction="vertical" style={{ width: '100%' }}>
                <Title level={5}>カテゴリ別設定</Title>
                {preferences.map((pref) =>
                  renderCategoryRow(pref.category, pref.channelInApp, pref.channelEmail)
                )}
              </Space>
            </Card>

            <Card title="配信設定">
              <Row gutter={[16, 16]} align="middle">
                <Col xs={24} md={8}>
                  <Space>
                    <Text>まとめて配信</Text>
                    <Switch
                      checked={digestEnabled}
                      loading={pending || updatePreferenceMutation.isPending}
                      onChange={(checked) => void handleDigestToggle(checked)}
                      aria-label="まとめて配信"
                    />
                  </Space>
                </Col>
                <Col xs={24} md={16}>
                  <Space>
                    <Text>配信頻度</Text>
                    <Radio.Group
                      aria-label="配信頻度"
                      options={DIGEST_OPTIONS}
                      optionType="button"
                      value={digestFrequency}
                      onChange={(event) => void handleDigestFrequencyChange(event.target.value)}
                      disabled={!digestEnabled || updatePreferenceMutation.isPending}
                    />
                  </Space>
                </Col>
              </Row>
            </Card>

            <Card title="休止時間">
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Space align="center">
                  <Text>休止時間を有効にする</Text>
                  <Switch
                    checked={quietHoursEnabled}
                    onChange={(checked) => void handleQuietHoursToggle(checked)}
                    loading={pending || updatePreferenceMutation.isPending}
                    aria-label="休止時間"
                  />
                </Space>
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12}>
                    <Space>
                      <Text>開始時刻</Text>
                      <TimePicker
                        value={quietHoursStart}
                        format={timeFormat}
                        onChange={(value) => void handleQuietHoursChange('start', value)}
                        disabled={!quietHoursEnabled}
                        aria-label="休止時間開始"
                      />
                    </Space>
                  </Col>
                  <Col xs={24} md={12}>
                    <Space>
                      <Text>終了時刻</Text>
                      <TimePicker
                        value={quietHoursEnd}
                        format={timeFormat}
                        onChange={(value) => void handleQuietHoursChange('end', value)}
                        disabled={!quietHoursEnabled}
                        aria-label="休止時間終了"
                      />
                    </Space>
                  </Col>
                </Row>
                <Text type="secondary">
                  休止時間中はメールおよびサイト内通知を受信しません。重要な通知は翌営業日に送信されます。
                </Text>
              </Space>
            </Card>

            <Card>
              <Space size="middle">
                <Button
                  type="primary"
                  ghost
                  onClick={() => void utils.notificationPreferences.list.invalidate()}
                >
                  最新の設定を取得
                </Button>
                <Text type="secondary">
                  個別に変更した値は即時に保存されます。必要に応じて再読み込みしてください。
                </Text>
              </Space>
            </Card>
          </Space>
        )}
      </Spin>
    </>
  );
}
